import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import { supabase, Event, Objective } from './lib/supabase';
import { useAuth } from './context/AuthContext';
import Sidebar from './components/Sidebar';
import Overview from './components/Overview';
import Calendar from './components/Calendar';
import Objectives from './components/Objectives';
import EventsList from './components/EventsList';
import Login from './pages/Login';

type Section = 'overview' | 'calendar' | 'objectives' | 'add-event';

function App() {
  const { user, loading: authLoading } = useAuth();
  const [isMobile, setIsMobile] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [events, setEvents] = useState<Event[]>([]);
  const [objectives, setObjectives] = useState<Objective[]>([]);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && !user && location.pathname !== '/login') {
      navigate('/login');
    }
  }, [user, authLoading, navigate, location]);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 700;
      setIsMobile(mobile);
      setIsSidebarOpen(!mobile);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isMobile && isSidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobile, isSidebarOpen]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [eventsRes, objectivesRes] = await Promise.all([
        supabase.from('events').select('*').order('start_date', { ascending: true }),
        supabase.from('objectives').select('*').order('created_at', { ascending: false })
      ]);

      if (eventsRes.data) setEvents(eventsRes.data as Event[]);
      if (objectivesRes.data) setObjectives(objectivesRes.data as Objective[]);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddEvent = async (event: Omit<Event, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const { data, error } = await supabase
        .from('events')
        .insert([event])
        .select()
        .single();

      if (error) throw error;
      if (data) {
        setEvents([...events, data as Event]);
        navigate('/calendar');
      }
    } catch (error) {
      console.error('Error adding event:', error);
      alert('Failed to add event. Please try again.');
    }
  };

  const handleUpdateEvent = async (id: string, updates: Partial<Event>) => {
    try {
      // Clean up updates - convert undefined to null for Supabase (Supabase uses null, not undefined)
      const cleanUpdates: Record<string, any> = {};
      Object.entries(updates).forEach(([key, value]) => {
        if (value !== undefined) {
          // Convert empty strings and undefined to null for Supabase
          cleanUpdates[key] = value === '' || value === undefined ? null : value;
        }
      });

      const { data, error } = await supabase
        .from('events')
        .update(cleanUpdates)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }
      
      if (data) {
        setEvents(events.map(e => e.id === id ? data as Event : e));
      } else {
        throw new Error('No data returned from update');
      }
    } catch (error: any) {
      console.error('Error updating event:', error);
      const errorMessage = error?.message || error?.details || 'Failed to update event. Please try again.';
      alert(`Failed to update event: ${errorMessage}`);
      throw error;
    }
  };

  const handleDeleteEvent = async (id: string) => {
    try {
      const { error } = await supabase.from('events').delete().eq('id', id);
      if (error) throw error;
      setEvents(events.filter(e => e.id !== id));
    } catch (error) {
      console.error('Error deleting event:', error);
      alert('Failed to delete event. Please try again.');
      throw error;
    }
  };

  const handleAddObjective = async (objective: Omit<Objective, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const { data, error } = await supabase
        .from('objectives')
        .insert([objective])
        .select()
        .single();

      if (error) throw error;
      if (data) {
        setObjectives([data as Objective, ...objectives]);
      }
    } catch (error) {
      console.error('Error adding objective:', error);
      alert('Failed to add objective. Please try again.');
    }
  };

  const handleUpdateObjective = async (id: string, updates: Partial<Objective>) => {
    try {
      const { data, error } = await supabase
        .from('objectives')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      if (data) {
        setObjectives(objectives.map(o => o.id === id ? data as Objective : o));
      }
    } catch (error) {
      console.error('Error updating objective:', error);
      alert('Failed to update objective. Please try again.');
    }
  };

  const handleDeleteObjective = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this objective?')) return;
    
    try {
      const { error } = await supabase.from('objectives').delete().eq('id', id);
      if (error) throw error;
      setObjectives(objectives.filter(o => o.id !== id));
    } catch (error) {
      console.error('Error deleting objective:', error);
      alert('Failed to delete objective. Please try again.');
    }
  };

  const getCurrentSection = (): Section => {
    const path = location.pathname;
    if (path === '/calendar') return 'calendar';
    if (path === '/objectives') return 'objectives';
    if (path === '/add-event') return 'add-event';
    return 'overview';
  };

  const currentSection = getCurrentSection();

  if (authLoading) {
    return <div className="loading">Loading...</div>;
  }

  if (location.pathname === '/login') {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
      </Routes>
    );
  }

  return (
    <div className="app">
      <div className="mobile-topbar">
        <button 
          className="hamburger" 
          onClick={() => setIsSidebarOpen(v => !v)}
          aria-label={isSidebarOpen ? 'Close menu' : 'Open menu'}
        >
          {isSidebarOpen ? '✕' : '☰'}
        </button>
        <div>2026 Roadmap</div>
      </div>
      
      <Sidebar
        currentSection={currentSection}
        isOpen={isSidebarOpen}
        isMobile={isMobile}
        onClose={() => setIsSidebarOpen(false)}
      />

      <main className="main">
        <Routes>
          <Route path="/" element={
            <>
              <div className="header">
                <h2 className="title">2026 Roadmap & Events</h2>
                <p className="subtitle">AirPLAi Sports • Company Events & Objectives</p>
              </div>
              <div className="content">
                {loading ? (
                  <div className="loading">Loading...</div>
                ) : (
                  <Overview events={events} objectives={objectives} />
                )}
              </div>
            </>
          } />
          <Route path="/calendar" element={
            <>
              <div className="header">
                <h2 className="title">Events Calendar</h2>
                <p className="subtitle">AirPLAi Sports • Company Events & Objectives</p>
              </div>
              <div className="content">
                {loading ? (
                  <div className="loading">Loading...</div>
                ) : (
                  <Calendar
                    events={events}
                    onUpdateEvent={handleUpdateEvent}
                    onDeleteEvent={handleDeleteEvent}
                  />
                )}
              </div>
            </>
          } />
          <Route path="/objectives" element={
            <>
              <div className="header">
                <h2 className="title">2026 Objectives</h2>
                <p className="subtitle">AirPLAi Sports • Company Events & Objectives</p>
              </div>
              <div className="content">
                {loading ? (
                  <div className="loading">Loading...</div>
                ) : (
                  <Objectives
                    objectives={objectives}
                    onAddObjective={handleAddObjective}
                    onUpdateObjective={handleUpdateObjective}
                    onDeleteObjective={handleDeleteObjective}
                  />
                )}
              </div>
            </>
          } />
          <Route path="/events" element={
            <>
              <div className="header">
                <h2 className="title">Events</h2>
                <p className="subtitle">AirPLAi Sports • Company Events & Objectives</p>
              </div>
              <div className="content">
                {loading ? (
                  <div className="loading">Loading...</div>
                ) : (
                  <EventsList
                    events={events}
                    onAddEvent={handleAddEvent}
                    onUpdateEvent={handleUpdateEvent}
                    onDeleteEvent={handleDeleteEvent}
                  />
                )}
              </div>
            </>
          } />
        </Routes>
      </main>
    </div>
  );
}

export default App;
